import React, { useMemo, useRef } from 'react';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { useAtomValue, useSetAtom } from 'jotai';
import { useReviewMode } from '../../providers/review-mode-providers';
import { completedAnnotationsAtom, panelTabAtom, pendingAnnotationsAtom, reviewAnnotationsAtom, reviewStatsAtom } from '../atoms';
import { Classification } from '../classification';
import { REVIEW_ANNOTATION_STATUS, REVIEW_MODE_PANEL_TABS } from '../constants';
import useAnalyticsForReviewSidePanel from '../hooks/useAnalyticsForReviewSidePanel';
import { CircleCheckIcon } from '../icons/circle-check-icon';
import { PanelStateRenderer } from '../panel-state-renderer';
import { PanelContainer } from '../styled-component-overrides';
import { type ReviewAnnotationStatusT } from '../types';
import { ActionPanel } from './action-panel';
type Props = {
  selectedAnnotationId?: string;
};
export const PendingPanel = ({
  selectedAnnotationId
}: Props) => {
  const pendingAnnotations = useAtomValue(pendingAnnotationsAtom);
  const reviewStats = useAtomValue(reviewStatsAtom);
  const setPanelTab = useSetAtom(panelTabAtom);
  const setCompletedAnnotations = useSetAtom(completedAnnotationsAtom);
  const previousValidIndexRef = useRef(0);
  const {
    selectAnnotation
  } = useReviewMode();
  const {
    sendEventReviewAnnotationAccept,
    sendEventReviewAnnotationReject
  } = useAnalyticsForReviewSidePanel();
  const reviewAnnotations = useAtomValue(reviewAnnotationsAtom);
  const currentAnnotationIndex = useMemo(() => {
    const index = pendingAnnotations.findIndex(id => id === selectedAnnotationId);
    if (index !== -1) {
      // Found the annotation, update and store the valid index
      // eslint-disable-next-line react-hooks/refs
      previousValidIndexRef.current = index;
      return index;
    }
    // Not found, return the previous valid index (clamped to current array bounds)
    // eslint-disable-next-line react-hooks/refs
    const clampedIndex = Math.min(previousValidIndexRef.current, pendingAnnotations.length - 1);
    return Math.max(0, clampedIndex);
  }, [pendingAnnotations, selectedAnnotationId]);
  const handleCompleteAction = (type: ReviewAnnotationStatusT) => {
    const annotationId = pendingAnnotations[currentAnnotationIndex!];
    const annotation = annotationId ? reviewAnnotations[annotationId] : undefined;
    if (annotation && annotationId) {
      const payload = {
        objectId: annotationId,
        objectType: annotation.type || 'unknown',
        label: annotation.label || annotation.name || ''
      };
      if (type === REVIEW_ANNOTATION_STATUS.APPROVED) {
        sendEventReviewAnnotationAccept(payload);
      } else if (type === REVIEW_ANNOTATION_STATUS.REJECTED) {
        sendEventReviewAnnotationReject(payload);
      }
    }

    // @ts-expect-error todo(ts-migration) TS2345 Argument of type '(prev: CompletedAnnotationT[]) => { annotationId: string | undefined; status: ReviewAnnotationStatusT; }[]' is not assignable to parameter of type 'SetStateAction<CompletedAnnotation...
    setCompletedAnnotations(prev => [...prev, {
      annotationId,
      status: type
    }]);
    const nextAnnotationId = currentAnnotationIndex === pendingAnnotations.length - 1 ? pendingAnnotations[currentAnnotationIndex - 1] : pendingAnnotations[currentAnnotationIndex + 1];
    // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    selectAnnotation?.(nextAnnotationId);
  };
  const handleRevertCompletedAnnotation = () => {
    setCompletedAnnotations([]);
  };
  if (pendingAnnotations.length === 0) {
    return <PanelStateRenderer icon={<CircleCheckIcon />} title="No pending items" description={`You have rejected “${reviewStats.rejected}” labels and approved “${reviewStats.approved}” labels. Proceed with the appropriate action.`} actionButton={<>
            <Button kind={KIND.secondary} shape={SHAPE.pill} size={SIZE.compact} onClick={() => setPanelTab(REVIEW_MODE_PANEL_TABS.COMPLETED)}>
              View Completed
            </Button>
            <Button kind={KIND.secondary} shape={SHAPE.pill} size={SIZE.compact} onClick={() => handleRevertCompletedAnnotation()}>
              Revert Completed Annotation
            </Button>
          </>} />;
  }
  return <PanelContainer>
      <Classification annotationIds={pendingAnnotations} currentAnnotationIndex={currentAnnotationIndex} selectedAnnotationId={selectedAnnotationId} />
      <ActionPanel onAccept={() => handleCompleteAction(REVIEW_ANNOTATION_STATUS.APPROVED)} onReject={() => handleCompleteAction(REVIEW_ANNOTATION_STATUS.REJECTED)} />
    </PanelContainer>;
};